import gql from 'graphql-tag';
import { useRef } from 'react';
import className from 'classnames/bind';
import { Container, RevealWrapper, SectionHeader, RhLink } from '@components';
import { PricingFragment } from '@graphqlTypes';
import { TicketPriceFragment } from '@/fragments/TicketPrice';
import { getLinkData } from '@/core/utils';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';


import styles from './Pricing.module.scss';
const cx = className.bind(styles);

type PriceItem = PricingFragment['prices']['nodes'][number];

const PricingItem = ({ item, index, count }: { item: PriceItem, index: number, count: number }) => {
  if (item.__typename !== "TicketPrice") return;
  const { ticketPriceFields: { pricing, price }, title } = item;
  const ctaData = pricing?.cta ? getLinkData(pricing.cta) : null;

  return (
    <div className={
      cx([
        "price-item",
        { 'price-item--last-row': index > count - 3 } // last two items
      ])}>
    <div className={cx("price-item__header")}>
      <h3 className={cx("title", "title-lc-s")}>{pricing?.title || title}</h3>
      <p className={cx("price", "text-l")}>{price}</p>
    </div>
    <div className={cx("price-item__content")}>
      <p className={cx("body", "text-m")}>
        {pricing?.description}{" "}
        {ctaData && (
          <RhLink className={cx("link", "inline-link")} href={ctaData.url} target={ctaData.target}>
            {ctaData.label}
          </RhLink>
        )}
      </p>
    </div>
  </div>
  )
};

export default function Pricing({ headline, subhead, prices }: PricingFragment) {
  const showHeader = Boolean(headline || subhead);

  const { isDesktop } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  return (
    <section className={cx(["pricing"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          { showHeader && <SectionHeader
            headline={headline}
            subhead={subhead}
            isRevealEnabled={true}
            isInView={isInView}
          /> }
          <div className={cx("pricing__items")}>
            {prices?.nodes?.map((item, index) => (
              <PricingItem key={index} item={item} index={index} count={prices.nodes.length} />
            ))}
          </div>
        </Container>
      </RevealWrapper>
    </section>
  )
}

Pricing.fragments = {
  entry: gql`
    ${TicketPriceFragment}
    fragment Pricing on FlexiblePageContentContentPricingLayout {
      headline
      subhead
      prices {
        nodes {
          ...Ticket
        }
      }
    }
  `,
};